import { inject } from '@angular/core';
import { CanMatchFn, Route, Routes } from '@angular/router';
import { ModuleFlagsService } from '@shared-lib/foundry-shared/module-flags.service';
import { CUSTOM_SCREEN_LOADERS } from './custom-screen-loaders.generated';
import { AuthGuard, DefaultRouteGuard } from './shared/foundry-guard/keycloak-auth.guard';
// import { AuthGuard } from './shared/foundry-guard/keycloak-auth.guard';
import { DeamServiceComponent } from './modules/deam-service/deam-service.component';

type ComponentLoader = NonNullable<Route['loadComponent']>;

const matchCustomScreen: CanMatchFn = async (route) => {
  const flags = inject(ModuleFlagsService);
  await flags.load();
  return flags.isCustom(route.path);
};

function customizableScreen(
  path: string,
  baseComponent: ComponentLoader,
  data?: Record<string, unknown>,
): Routes {
  const customComponent = CUSTOM_SCREEN_LOADERS[path];
  return [
    ...(customComponent
      ? [{ path, loadComponent: customComponent, canMatch: [matchCustomScreen], canActivate: [AuthGuard], data }]
      : []),
    { path, loadComponent: baseComponent, canActivate: [AuthGuard], data },
  ];
}

const baseHome: ComponentLoader = () =>
  import('./modules/home/home-screen.component').then(({ HomeScreenComponent }) => HomeScreenComponent);
const baseDomainCatalogs: ComponentLoader = () =>
  import('./modules/domain-catalogs/domain-catalogs.component').then(({ DomainCatalogsComponent }) => DomainCatalogsComponent);

export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    canActivate: [DefaultRouteGuard],
    loadComponent: () => import('./modules/access-denied/access-denied.component')
      .then(({ AccessDeniedComponent }) => AccessDeniedComponent),
  },
  {
    path: 'access-denied',
    loadComponent: () => import('./modules/access-denied/access-denied.component')
      .then(({ AccessDeniedComponent }) => AccessDeniedComponent),
  },
  {
    path: 'session-expired',
    loadComponent: () => import('./modules/session-expired/session-expired.component')
      .then(({ SessionExpiredComponent }) => SessionExpiredComponent),
  },
  ...customizableScreen('main', baseHome, {
    configFile: 'assets/foundry-screens/home/main.json',
  }),
  ...customizableScreen('home-main', baseHome, {
    configFile: 'assets/foundry-screens/home/home-main.json',
  }),
  ...customizableScreen('spriced-home', baseHome, {
    configFile: 'assets/foundry-screens/home/spriced-home.json',
  }),
  ...customizableScreen(
    'approvals',
    () => import('./modules/approval-workflow-hub/approval-workflow-hub-screen.component')
      .then(({ ApprovalWorkflowHubScreenComponent }) => ApprovalWorkflowHubScreenComponent),
    { accessKey: 'approvals', configFile: 'assets/foundry-screens/spriced/approvals.json' },
  ),
  ...customizableScreen(
    'approval-workflows',
    () => import('./modules/approval-workflow-builder/approval-workflow-builder-screen.component')
      .then(({ ApprovalWorkflowBuilderScreenComponent }) => ApprovalWorkflowBuilderScreenComponent),
    { accessKey: 'approval-workflows', configFile: 'assets/foundry-screens/spriced/approval-workflows.json' },
  ),
  ...customizableScreen(
    'chain-list',
    () => import('./modules/workflow-sequences/workflow-sequence-list-screen.component')
      .then(({ WorkflowSequenceListScreenComponent }) => WorkflowSequenceListScreenComponent),
    { accessKey: 'chain-list', configFile: 'assets/foundry-screens/spriced/chain-list.json' },
  ),
  ...customizableScreen(
    'workflow-chain',
    () => import('./modules/workflow-sequences/workflow-chain-screen.component')
      .then(({ WorkflowChainScreenComponent }) => WorkflowChainScreenComponent),
    { accessKey: 'workflow-chain', configFile: 'assets/foundry-screens/spriced/workflow-chain.json' },
  ),
  ...customizableScreen(
    'price-recommendation',
    () => import('./modules/price-recommendation/price-recommendation.component')
      .then(({ PriceRecommendationScreenComponent }) => PriceRecommendationScreenComponent),
  ),
  ...customizableScreen(
    'user-access',
    () => import('./modules/user-access/user-access.component').then(({ UserAccessComponent }) => UserAccessComponent),
  ),
  {
    path: 'deam-service',
    component: DeamServiceComponent,
    canActivate: [AuthGuard],
    data: { accessKey: 'deam-service', configFile: 'assets/foundry-screens/spriced/deam-service.json' },
  },
  ...customizableScreen(
    'audit',
    () => import('./modules/audit/audit.component').then(({ AuditComponent }) => AuditComponent),
    { accessKey: 'audit', configFile: 'assets/foundry-screens/spriced/audit.json' },
  ),
  ...customizableScreen(
    'discount-templates',
    () => import('./custom/discount-templates/discount-templates.component')
      .then(({ DiscountTemplatesComponent }) => DiscountTemplatesComponent),
    { accessKey: 'discount-templates', configFile: 'assets/foundry-screens/spriced/chetan.json' },
  ),
  {
    path: 'discount-request',
    loadComponent: () => import('./custom/discount-request-workspace/discount-request-workspace.component')
      .then(({ DiscountRequestWorkspaceComponent }) => DiscountRequestWorkspaceComponent),
    canActivate: [AuthGuard],
    // Discount Request belongs to the Discount Templates capability, so use
    // the established permission key already configured by the home card.
    data: { accessKey: 'discount-templates', configFile: 'assets/config/discount-request-workspace.json' },
  },
  ...customizableScreen('domain-catalogs', baseDomainCatalogs, {
    configFile: 'assets/foundry-screens/spriced/item-catalogs.json',
  }),
  ...customizableScreen('customer-catalogs', baseDomainCatalogs, {
    configFile: 'assets/foundry-screens/spriced/customer-catalogs.json',
  }),
  ...customizableScreen('price-list-catalogs', baseDomainCatalogs, {
    configFile: 'assets/foundry-screens/spriced/price-list-catalogs.json',
  }),
  { path: '**', redirectTo: 'access-denied' },
  ...customizableScreen('ttm-list', () => import('./modules/ttm-list/ttm-list-screen.component').then(m => m.TtmListScreenComponent), { accessKey: 'ttm-list', configFile: 'assets/foundry-screens/spriced/ttm-list.json' }),
  ...customizableScreen('ttm', () => import('./modules/ttm/ttm.component').then(m => m.TtmComponent), { accessKey: 'ttm', configFile: 'assets/foundry-screens/spriced/ttm.json' }),
  { path: '**', redirectTo: 'home-main' },
];
